import React, { useMemo } from 'react';
import { CheckBadgeIcon } from '@heroicons/react/20/solid';
import type {
  PerformanceRecord,
  PerfVerificationStatus,
} from '../../types/buildExpansion';
import { PerformanceSheetImage } from './PerformanceSheetImage';

/**
 * Non-dyno performance records grouped by record type. Verification status is
 * always explicit: owner-reported numbers are labeled "Owner reported" and
 * are never presented as verified.
 */

const formatDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

interface VerificationPresentation {
  text: string;
  verified: boolean;
}

const VERIFICATION_PRESENTATION: Record<PerfVerificationStatus, VerificationPresentation> = {
  owner_reported: { text: 'Owner reported', verified: false },
  evidence_attached: { text: 'Evidence attached', verified: false },
  shop_verified: { text: 'Verified · shop', verified: true },
  event_verified: { text: 'Verified · event', verified: true },
  admin_verified: { text: 'Verified · admin', verified: true },
};

/** Explicit verification chip shared by performance and dyno displays. */
export const VerificationChip: React.FC<{ status: PerfVerificationStatus }> = ({ status }) => {
  const presentation = VERIFICATION_PRESENTATION[status] || VERIFICATION_PRESENTATION.owner_reported;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium border ${
        presentation.verified
          ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/25'
          : 'bg-[#111] text-gray-400 border-white/[0.08]'
      }`}
    >
      {presentation.verified && <CheckBadgeIcon className="w-3 h-3" aria-hidden />}
      {presentation.text}
    </span>
  );
};

/** Value string for a record, preferring the backend display value. */
const formatRecordValue = (record: PerformanceRecord): string | null => {
  if (record.display_value) return record.display_value;
  if (record.value_primary == null) return null;
  return `${record.value_primary}${record.unit_primary ? ` ${record.unit_primary}` : ''}`;
};

export interface PerformanceRecordsProps {
  records: PerformanceRecord[];
  className?: string;
}

export const PerformanceRecords: React.FC<PerformanceRecordsProps> = ({
  records,
  className = '',
}) => {
  const groups = useMemo(() => {
    const map = new Map<string, { label: string; items: PerformanceRecord[] }>();
    records
      .filter((r) => r.record_type !== 'dyno')
      .forEach((record) => {
        const existing = map.get(record.record_type);
        if (existing) {
          existing.items.push(record);
        } else {
          map.set(record.record_type, {
            label: record.record_type_label || record.record_type,
            items: [record],
          });
        }
      });
    return Array.from(map.values());
  }, [records]);

  if (groups.length === 0) return null;

  return (
    <section className={`card p-5 sm:p-7 space-y-5 ${className}`} aria-label="Performance records">
      <div className="build-section-intro border-b border-white/[0.06] pb-3">
        <h3 className="font-display text-base sm:text-lg font-semibold tracking-tight text-white">
          Performance records
        </h3>
      </div>

      {groups.map((group) => (
        <div key={group.label}>
          <h4 className="spec-label">{group.label}</h4>
          <ul className="mt-2 space-y-2 list-none">
            {group.items.map((record) => {
              const value = formatRecordValue(record);
              const date = formatDate(record.date_recorded);
              const context = [record.venue_name, record.layout_name].filter(Boolean).join(' · ');
              const position =
                record.result_position != null
                  ? `P${record.result_position}${record.field_size != null ? ` of ${record.field_size}` : ''}`
                  : null;
              const metaParts = [context, position, date, record.timing_method]
                .filter(Boolean)
                .join(' · ');

              return (
                <li
                  key={record.id}
                  className="rounded-lg border border-white/[0.06] bg-[#0c0c0c]/50 px-3.5 py-3"
                >
                  <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                      <PerformanceSheetImage
                        url={record.sheet_image_url}
                        alt={
                          record.record_type === 'dyno'
                            ? 'Dyno graph'
                            : `${record.record_type_label || 'Performance'} result photo`
                        }
                      />
                      <div className="min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
                        {value && (
                          <span className="font-display text-xl font-semibold tracking-tight text-white tabular-nums">
                            {value}
                          </span>
                        )}
                        {record.title && (
                          <span className="text-sm text-gray-300">{record.title}</span>
                        )}
                      </div>
                      {metaParts && (
                        <p className="font-mono text-[11px] text-gray-500 mt-1">{metaParts}</p>
                      )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                      {record.is_personal_best && (
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium bg-orange-500/10 text-orange-300 border border-orange-500/25">
                          Personal best
                        </span>
                      )}
                      <VerificationChip status={record.verification_status} />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
};

export default PerformanceRecords;
